import { FormStep } from '@sec-loans-ui/loan-offer/steps/FormStep'
import {
  ENDPOINT_GET_LOAN_OFFER_DOCUMENT,
  LoanOfferDocumentType,
  LoanOfferMode,
  LoanOfferReason,
  SecLoansSource,
} from '@shared/sec-loans'
import { CommonMimeType } from '@shared/types'
import { useWizardForm } from '@ui-common/hooks'
import { callEndpoint, debugLog, getFilename, isErrorResponse } from '@ui-common/utils'
import { downloadAndOpenBase64 } from '@ui-common/utils/file/fileDownload'
import { Button } from '@ui-components/actions/Button'
import { BulletIcon } from '@ui-components/assets/Icons'
import { Notification } from '@ui-components/containers/Notification'
import { Divider } from '@ui-components/decorative/Divider'
import { IndentationText } from '@ui-components/decorative/IndentationText'
import { LoadingSpinner } from '@ui-components/decorative/LoadingSpinner'
import { StepSummaryView } from '@ui-components/form/StepSummaryView'
import { FlexColumnContainer, FlexRowContainer } from '@ui-components/global/CommonStyling'
import { SecondaryHeading } from '@ui-components/typography/headings/SecondaryHeading'
import { AdditionalText, LabelText } from '@ui-components/typography/Typography'
import { useState } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { getSecLoansSourceChannel } from '../../../utils/helpers'
import { ReservationFooter } from '../../components/ReservationFooter'
import { useLoanOfferFormContext } from '../formContext'
import { useExistingLoanOfferContext } from '../LoanOfferContext'
import { getOfferSummaryData } from './offerStepUtils'

const AdditionalContent = styled.div`
  padding: 1rem 0 0 1rem;
`

const ButtonWrapper = styled(FlexRowContainer)`
  > span {
    align-self: center;
  }
  button {
    flex-basis: fit-content;
    text-align: start;
  }
`

export const OfferStep = () => {
  const intl = useIntl()
  const { proceed, loanOffer, user } = useExistingLoanOfferContext()
  const methods = useWizardForm()

  const offerSummaryData = loanOffer.offeredLoans.map((offerReason: LoanOfferReason) =>
    getOfferSummaryData(user, offerReason, loanOffer, intl),
  )

  const isAutomaticOffer = loanOffer.loanOfferMode === LoanOfferMode.Automatic
  const notificationText = isAutomaticOffer
    ? intl.formatMessage({ id: 'loan-offer-automatic-offer-description' })
    : intl.formatMessage({ id: 'loan-offer-manual-offer-description' }, { bankerName: loanOffer.banker?.name })

  return (
    <FormStep
      allowBack={false}
      subHeader={intl.formatMessage({ id: 'loan-offer-offer-step-heading' })}
      formMethods={methods}
      onSubmit={proceed}
      customFooter={
        <ReservationFooter loanOfferMode={loanOffer.loanOfferMode} reservationLink={loanOffer.reservationLink} />
      }
    >
      <Notification header={notificationText} status={'info'} />
      {offerSummaryData.map((item) => (
        <Fragment key={item.title}>
          <Divider />
          <SecondaryHeading header={item.title} headerCaption={item.purpose} />
          {item.coDebtorName && (
            <IndentationText>
              <FlexColumnContainer>
                <LabelText>{intl.formatMessage({ id: 'loan-offer-secondary-applicant-label' })}</LabelText>
                <AdditionalText>{item.coDebtorName}</AdditionalText>
              </FlexColumnContainer>
            </IndentationText>
          )}
          {item.targetData.length > 0 && (
            <StepSummaryView
              heading={intl.formatMessage({ id: 'sec-loans-phases-loan-target-phase-header' })}
              data={item.targetData}
              isLabelUnique={true}
              twoColumnLayoutSupport={true}
            />
          )}
          <StepSummaryView
            heading={intl.formatMessage({ id: 'loan-offer-offer-basic-information-label' })}
            data={item.offerData}
            isLabelUnique={true}
            twoColumnLayoutSupport={true}
          />
          <FlexColumnContainer>
            <SecondaryHeading header={intl.formatMessage({ id: 'loan-offer-offer-additional-information-label' })} />
            <AdditionalContent>
              {item.shouldShowEsisLink && (
                <DocumentLink
                  offeredLoanId={item.offeredLoanId}
                  documentType={LoanOfferDocumentType.Esis}
                  linkText={intl.formatMessage({ id: 'loan-offer-offer-esis-link-text' })}
                />
              )}
              {item.shouldShowVekLink && (
                <DocumentLink
                  offeredLoanId={item.offeredLoanId}
                  documentType={LoanOfferDocumentType.Vek}
                  linkText={intl.formatMessage({ id: 'loan-offer-offer-vek-link-text' })}
                />
              )}
              {
                <DocumentLink
                  offeredLoanId={item.offeredLoanId}
                  documentType={LoanOfferDocumentType.AmortizationTable}
                  linkText={intl.formatMessage({ id: 'loan-offer-offer-amortization-table-link-text' })}
                />
              }
            </AdditionalContent>
          </FlexColumnContainer>
        </Fragment>
      ))}
    </FormStep>
  )
}

const DocumentLink = (props: { offeredLoanId: string; linkText: string; documentType: LoanOfferDocumentType }) => {
  const intl = useIntl()
  const { openErrorModal } = useLoanOfferFormContext()

  const [isLoadingDocument, setIsLoadingDocument] = useState(false)

  const downloadDocument = async (documentType: LoanOfferDocumentType, offeredLoanId: string) => {
    try {
      setIsLoadingDocument(true)
      debugLog(`Download document ${documentType}`, ENDPOINT_GET_LOAN_OFFER_DOCUMENT, offeredLoanId)
      const base64EncodedPdf = await callEndpoint(ENDPOINT_GET_LOAN_OFFER_DOCUMENT, {
        queryParams: { offeredLoanId, documentType },
      })
      downloadAndOpenBase64({
        base64Encoded: base64EncodedPdf,
        documentInformation: {
          mimeType: CommonMimeType.Pdf,
          fileName: getFilename(),
        },
        useDataUrl: getSecLoansSourceChannel() === SecLoansSource.SMobile,
      })
    } catch (error) {
      console.error('Error while downloading document', error)
      const requestId = isErrorResponse(error) ? error.requestId : undefined
      openErrorModal(requestId)
    } finally {
      setIsLoadingDocument(false)
    }
  }

  return (
    <ButtonWrapper>
      <BulletIcon />
      <Button
        label={props.linkText}
        variant="plain"
        iconPos="after"
        icon={
          isLoadingDocument ? (
            <LoadingSpinner size="small" alt={intl.formatMessage({ id: 'common-wait-description' })} />
          ) : undefined
        }
        onClick={isLoadingDocument ? undefined : () => downloadDocument(props.documentType, props.offeredLoanId)}
        disabled={isLoadingDocument}
      />
    </ButtonWrapper>
  )
}
